/**
 * Onboarding API Client Service
 * Base URL: https://hellovedis-shopify-server.vercel.app/api
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.OnboardingAPI = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  const API_BASE_URL = "https://hellovedis-shopify-server.vercel.app/api";
  const USER_ID_KEY = "onboarding_userId";

  async function request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = options.headers || {};

    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        const errorMsg = data.message || (data.errors ? JSON.stringify(data.errors) : `Request failed with status ${response.status}`);
        throw new Error(errorMsg);
      }

      return data;
    } catch (err) {
      console.error(`[API Error] ${options.method || 'GET'} ${endpoint}:`, err.message);
      throw err;
    }
  }

  function getStoredUserId() {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(USER_ID_KEY);
    }
    return null;
  }

  function setStoredUserId(id) {
    if (typeof localStorage !== 'undefined' && id) {
      localStorage.setItem(USER_ID_KEY, id);
    }
  }

  function clearStoredUserId() {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(USER_ID_KEY);
    }
    if (typeof document !== 'undefined') {
      document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
    }
  }

  /**
   * Constructs exact JSON payload structure:
   * - lead_info: name, phone, calling_phone, alternate_phone, country_code, preferred_language, call_number_confirmation
   * - user_profile: gender, age
   * - hair_health_assessment: hair_concern (array), hair_loss_duration, hair_stage, scalp_type, dandruff, wash_frequency
   * - lifestyle_and_genetics: family_history, water_type, stress_level, digestive_health (array), medical_conditions (array)
   * - diagnosis_assets: scalp_photos (array of Cloudflare / CDN image URLs)
   * - consultation_booking: appointment_date, appointment_time_slot, formatted_schedule_text, booking_status
   */
  function buildPayloadFromState(stateAnswers = {}, bookingState = {}) {
    const payload = {};

    // 1. Lead Info Section
    const lead_info = {};
    if (stateAnswers.name) lead_info.name = stateAnswers.name;
    if (stateAnswers.phone || bookingState.phone) lead_info.phone = String(stateAnswers.phone || bookingState.phone).replace(/\D/g, "").slice(-10);
    if (stateAnswers.calling_phone) lead_info.calling_phone = String(stateAnswers.calling_phone).replace(/\D/g, "").slice(-10);
    const alt = stateAnswers.alt_phone || stateAnswers.altPhone || bookingState.altPhone;
    if (alt) lead_info.alternate_phone = String(alt).replace(/\D/g, "").slice(-10);
    lead_info.country_code = "+91";
    const lang = stateAnswers.language || bookingState.language;
    if (lang) lead_info.preferred_language = lang;
    lead_info.call_number_confirmation = stateAnswers.call_number_confirmation !== undefined ? Boolean(stateAnswers.call_number_confirmation) : true;
    
    if (Object.keys(lead_info).length > 0) payload.lead_info = lead_info;

    // 2. User Profile Section
    const user_profile = {};
    if (stateAnswers.gender) user_profile.gender = stateAnswers.gender;
    if (stateAnswers.age) user_profile.age = Number(stateAnswers.age);
    if (Object.keys(user_profile).length > 0) payload.user_profile = user_profile;

    // 3. Hair Health Assessment Section
    const hair_assessment = {};
    if (Array.isArray(stateAnswers.hair_concern)) {
      hair_assessment.hair_concern = stateAnswers.hair_concern;
    } else if (stateAnswers.hair_concern) {
      hair_assessment.hair_concern = [stateAnswers.hair_concern];
    }
    if (stateAnswers.hair_loss_duration) hair_assessment.hair_loss_duration = stateAnswers.hair_loss_duration;
    if (stateAnswers.hair_stage) hair_assessment.hair_stage = stateAnswers.hair_stage;
    if (stateAnswers.scalp_type) hair_assessment.scalp_type = stateAnswers.scalp_type;
    if (stateAnswers.dandruff) hair_assessment.dandruff = stateAnswers.dandruff;
    if (stateAnswers.wash_frequency) hair_assessment.wash_frequency = stateAnswers.wash_frequency;
    if (Object.keys(hair_assessment).length > 0) payload.hair_health_assessment = hair_assessment;

    // 4. Lifestyle & Genetics Section
    const lifestyle = {};
    if (stateAnswers.family_history) lifestyle.family_history = stateAnswers.family_history;
    if (stateAnswers.water_type) lifestyle.water_type = stateAnswers.water_type;
    if (stateAnswers.stress_level !== undefined && stateAnswers.stress_level !== null) lifestyle.stress_level = Number(stateAnswers.stress_level);
    
    if (Array.isArray(stateAnswers.digestive_health)) {
      lifestyle.digestive_health = stateAnswers.digestive_health;
    } else if (stateAnswers.digestive_health) {
      lifestyle.digestive_health = [stateAnswers.digestive_health];
    }

    if (Array.isArray(stateAnswers.medical_conditions)) {
      lifestyle.medical_conditions = stateAnswers.medical_conditions;
    } else if (stateAnswers.medical_conditions) {
      lifestyle.medical_conditions = [stateAnswers.medical_conditions];
    }

    if (Object.keys(lifestyle).length > 0) payload.lifestyle_and_genetics = lifestyle;

    // 5. Diagnosis Assets Section (scalp_photos as array of Cloudflare / CDN image URLs)
    const scalpPhotos = [];
    if (stateAnswers.scalp_photo_url_0) scalpPhotos.push(stateAnswers.scalp_photo_url_0);
    if (stateAnswers.scalp_photo_url_1) scalpPhotos.push(stateAnswers.scalp_photo_url_1);
    if (stateAnswers.scalp_photo_url && !scalpPhotos.includes(stateAnswers.scalp_photo_url)) {
      scalpPhotos.push(stateAnswers.scalp_photo_url);
    }
    if (Array.isArray(stateAnswers.scalp_photos)) {
      stateAnswers.scalp_photos.forEach(url => {
        if (url && typeof url === "string" && (url.startsWith("http://") || url.startsWith("https://"))) {
          if (!scalpPhotos.includes(url)) scalpPhotos.push(url);
        }
      });
    }

    // Fallback: Check photo_upload if contains http URLs
    if (Array.isArray(stateAnswers.photo_upload)) {
      stateAnswers.photo_upload.forEach(item => {
        if (item && typeof item === "string" && (item.startsWith("http://") || item.startsWith("https://"))) {
          if (!scalpPhotos.includes(item)) scalpPhotos.push(item);
        }
      });
    }

    if (scalpPhotos.length > 0) {
      payload.diagnosis_assets = {
        scalp_photos: scalpPhotos
      };
    }

    // 6. Consultation Booking Section
    if (bookingState.slot || bookingState.dateISO || stateAnswers.appointment_date) {
      payload.consultation_booking = {
        appointment_date: bookingState.dateISO || stateAnswers.appointment_date || "",
        appointment_time_slot: bookingState.slot ? `${bookingState.slot} – ${bookingState.slotEndTime || ""}` : (stateAnswers.appointment_slot || ""),
        formatted_schedule_text: bookingState.dateDisplay ? `${bookingState.dateDisplay}, ${bookingState.slot}` : "",
        booking_status: bookingState.isConfirmed ? "CONFIRMED" : "PENDING"
      };
    }

    return payload;
  }

  return {
    getStoredUserId,
    setStoredUserId,
    clearStoredUserId,
    buildPayloadFromState,

    saveBooking: async function (userId, bookingState) {
      const id = userId || getStoredUserId();
      if (!id) return null;

      const payload = {
        consultation_booking: {
          appointment_date: bookingState.dateISO || "",
          appointment_time_slot: bookingState.slot ? `${bookingState.slot} – ${bookingState.slotEndTime || ""}` : "",
          formatted_schedule_text: bookingState.dateDisplay ? `${bookingState.dateDisplay}, ${bookingState.slot}` : "",
          booking_status: bookingState.isConfirmed ? "CONFIRMED" : "PENDING"
        }
      };

      if (bookingState.altPhone) {
        payload.lead_info = {
          alternate_phone: String(bookingState.altPhone).replace(/\D/g, "").slice(-10)
        };
      }

      return await request(`/users/${id}/onboarding`, {
        method: "PATCH",
        body: JSON.stringify(payload)
      });
    },

    startUser: async function (leadInfo, userProfile) {
      const body = {
        lead_info: leadInfo,
        user_profile: userProfile
      };
      const res = await request("/users/start", {
        method: "POST",
        body: JSON.stringify(body)
      });

      if (res.data && res.data.user && res.data.user._id) {
        setStoredUserId(res.data.user._id);
      }
      return res;
    },

    saveOnboarding: async function (userId, payload) {
      const id = userId || getStoredUserId();
      if (!id) throw new Error("No userId available to save onboarding data.");
      return await request(`/users/${id}/onboarding`, {
        method: "PATCH",
        body: JSON.stringify(payload)
      });
    },

    saveAnswer: async function (userId, questionId, answer) {
      const id = userId || getStoredUserId();
      if (!id) return null;
      return await request(`/users/${id}/answer`, {
        method: "PATCH",
        body: JSON.stringify({ questionId, answer })
      });
    },

    uploadPhoto: async function (userId, fileOrBlob) {
      const id = userId || getStoredUserId();
      if (!id) throw new Error("No userId available to upload photo.");
      const formData = new FormData();
      formData.append("photo", fileOrBlob);

      return await request(`/users/${id}/photo`, {
        method: "POST",
        body: formData
      });
    },

    getUser: async function (userId) {
      const id = userId || getStoredUserId();
      if (!id) return null;
      return await request(`/users/${id}`, { method: "GET" });
    },

    getProgress: async function (userId) {
      const id = userId || getStoredUserId();
      if (!id) return null;
      return await request(`/users/${id}/progress`, { method: "GET" });
    },

    completeOnboarding: async function (userId) {
      const id = userId || getStoredUserId();
      if (!id) return null;
      const res = await request(`/users/${id}/complete`, { method: "PATCH" });
      clearStoredUserId();
      return res;
    }
  };
}));
